import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 음성입력(STT) — 브라우저 내장 Web Speech API (기능명세 Z-1-3).
 *
 * 서버 STT를 쓰지 않는다: 변환이 브라우저에서 끝나고 결과는 textarea 값으로만 들어가므로
 * BE 계약이 없다(§3.9는 `reportContent` 문자열만 받는다 — 타이핑과 구분하지 않는다).
 *
 * 미지원 브라우저(iOS Safari 등)에서는 `supported=false` → 호출부가 마이크 버튼을 숨기고
 * 타이핑으로 폴백한다. 무음 10초가 지나면 자동 종료한다(기획 시안 카운트다운).
 */

/** 표준 타입에 없는 벤더 API — 필요한 표면만 최소 선언 */
interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  readonly length: number;
  isFinal: boolean;
  [index: number]: SpeechRecognitionAlternativeLike;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    readonly length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}
type RecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): RecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** 무음 자동종료(초) — 기획 시안 카운트다운 */
const SILENCE_SEC = 10;

interface UseSpeechToTextOptions {
  /** 확정된 문장을 받는다. 호출부가 textarea 값에 이어붙인다. */
  onFinalText: (text: string) => void;
}

export function useSpeechToText({ onFinalText }: UseSpeechToTextOptions) {
  const [supported] = useState(() => getCtor() !== null);
  const [listening, setListening] = useState(false);
  /** 무음 잔여 초 — 듣는 중이면 null(카운트다운 미표시) */
  const [silenceLeft, setSilenceLeft] = useState<number | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // onresult 안에서 최신 콜백을 보도록 ref 경유 — 재구독 없이 갱신된다.
  const onFinalRef = useRef(onFinalText);
  onFinalRef.current = onFinalText;

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setSilenceLeft(null);
  }, []);

  const stop = useCallback(() => {
    clearTimer();
    setListening(false);
    try {
      recognitionRef.current?.stop();
    } catch {
      /* 이미 정지 상태 — 무시 */
    }
  }, [clearTimer]);

  /** 무음 카운트다운 시작(재시작). 발화가 감지되면 호출부가 다시 불러 리셋한다. */
  const restartSilenceTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    let left = SILENCE_SEC;
    setSilenceLeft(left);
    timerRef.current = setInterval(() => {
      left -= 1;
      setSilenceLeft(left);
      if (left <= 0) stop();
    }, 1000);
  }, [stop]);

  const start = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor) return;

    const rec = new Ctor();
    rec.lang = 'ko-KR';
    rec.continuous = true;
    // 도중(interim) 결과도 받는다 — 글자로는 넣지 않고(확정문 isFinal 만 반영해 되돌림 처리를 없앤다) 말하는 동안
    // 무음 타이머를 리셋하는 신호로만 쓴다. 확정 결과만 받으면 쉬지 않고 길게 말할 때 결과가 안 와서 10초에 말하던 중 끊긴다.
    rec.interimResults = true;

    rec.onresult = (e) => {
      restartSilenceTimer(); // 도중 · 확정 결과 모두 발화 감지 → 무음 타이머 리셋
      let finalText = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
      }
      if (finalText) onFinalRef.current(finalText);
    };
    rec.onerror = () => stop();
    rec.onend = () => {
      clearTimer();
      setListening(false);
    };

    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
      restartSilenceTimer();
    } catch {
      setListening(false);
    }
  }, [restartSilenceTimer, stop, clearTimer]);

  const toggle = useCallback(() => {
    if (listening) stop();
    else start();
  }, [listening, start, stop]);

  // 화면 이탈 시 마이크 점유를 반드시 해제한다.
  useEffect(() => stop, [stop]);

  return { supported, listening, silenceLeft, toggle, stop };
}
